import React from 'react';
import { Card, CardContent, Typography, Box, Chip, IconButton, Tooltip, Button, useTheme } from '@mui/material';
import { 
  OpenInNew as OpenInNewIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
  Psychology as PsychologyIcon
} from '@mui/icons-material';
import { useNavigate, Link } from 'react-router-dom';

// Sample data - in a real app this would come from an API
const deployedAgents = [
  {
    id: 'pio-agent',
    name: 'PioAgent',
    type: 'Chat Application',
    status: 'online',
    model: 'meta-llama/Llama-3.1-8B-Instruct',
    requests: 1287,
    responseTime: 134,
    successRate: 99.8
  },
  {
    id: 'hiring-agent',
    name: 'HiringAgent',
    type: 'CV Processing',
    status: 'offline',
    model: 'deepseek-ai/DeepSeek-R1-Distill-Qwen-7B',
    requests: 457,
    responseTime: 156,
    successRate: 98.2
  },
  {
    id: 'sales-agent',
    name: 'SalesAgent',
    type: 'Customer Interaction',
    status: 'online',
    model: 'anthropic/claude-3-haiku',
    requests: 843,
    responseTime: 89,
    successRate: 99.5
  },
  {
    id: 'support-agent',
    name: 'SupportAgent',
    type: 'Customer Support',
    status: 'online',
    model: 'mistralai/Mistral-7B-Instruct-v0.2',
    requests: 1542,
    responseTime: 102,
    successRate: 97.8
  },
  {
    id: 'code-agent',
    name: 'CodeAssistant',
    type: 'Development',
    status: 'offline',
    model: 'bigcode/starcoder2-15b',
    requests: 329,
    responseTime: 187,
    successRate: 93.4
  },
  {
    id: 'research-agent',
    name: 'ResearchAgent',
    type: 'Data Analysis',
    status: 'online',
    model: 'meta-llama/Llama-3.1-70B-Instruct',
    requests: 124,
    responseTime: 310,
    successRate: 98.9
  }
];

const AgentItem = ({ agent, theme }) => {
  const navigate = useNavigate();
  const isOnline = agent.status === 'online';
  
  return (
    <Box 
      sx={{ 
        mb: 2,
        position: 'relative',
        p: 1.5,
        borderRadius: 2.5,
        transition: 'all 0.2s',
        border: `1px solid ${theme.palette.divider}`,
        '&:hover': {
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)',
          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
          transform: 'translateY(-1px)'
        }
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {isOnline ? (
            <CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: 16, mr: 1 }} />
          ) : (
            <ErrorOutlineIcon sx={{ color: 'text.disabled', fontSize: 16, mr: 1 }} />
          )}
          <Typography variant="body1" fontWeight={600} fontSize="0.95rem">
            {agent.name}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="View in Agent Studio">
            <IconButton
              size="small"
              sx={{
                color: 'text.secondary',
                p: 0.75,
                '&:hover': {
                  color: 'text.primary',
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
                }
              }}
              onClick={() => navigate('/agent-studio')}
            >
              <OpenInNewIcon fontSize="small" sx={{ fontSize: '1rem' }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Chip
          label={agent.type}
          size="small"
          sx={{
            backgroundColor: isOnline ? `${theme.palette.success.main}15` : theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.04)',
            color: isOnline ? theme.palette.success.main : 'text.secondary',
            fontSize: '0.7rem',
            height: 20,
            fontWeight: 500,
            '& .MuiChip-label': { px: 1, py: 0 }
          }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: agent.hasApiKey ? theme.palette.success.main : theme.palette.warning.main
            }}
          />
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
            {agent.hasApiKey ? 'API Key Set' : 'No API Key'}
          </Typography>
        </Box>
      </Box>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
          Endpoint: {agent.endpoint}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
          Created: {agent.createdAt}
        </Typography>
      </Box>
    </Box>
  );
};

// Transform real agent data based on actual API response
const transformAgentData = (agent) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Unknown';
    }
  };

  const getEndpointType = (endpoint) => {
    if (endpoint?.includes('chatbotcost')) return 'Cost Calculator';
    if (endpoint?.includes('chatbot')) return 'Chatbot';
    return 'AI Agent';
  };

  return {
    id: agent.id,
    name: agent.name || 'Unnamed Agent',
    type: getEndpointType(agent.endpoint),
    status: agent.isActive ? 'online' : 'offline',
    endpoint: agent.endpoint || 'Unknown',
    createdAt: formatDate(agent.createdAt),
    updatedAt: formatDate(agent.updatedAt),
    // Remove fake metrics since they're not available in the API
    hasApiKey: !!agent.apiKey
  };
};

export default function DeployedAgents({ agents = [], hasAccess }) {
  const navigate = useNavigate();
  const theme = useTheme();
  return (
    <Card sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'background.paper',
      color: 'text.primary',
      borderRadius: 3,
      boxShadow: 'none',
      border: `1px solid ${theme.palette.divider}`
    }}>
      <CardContent sx={{ 
        p: 0,
        '&:last-child': { pb: 0 },
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
      }}>
        <Box sx={{ p: 2, pb: 1.5, borderBottom: `1px solid ${theme.palette.divider}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem', mb: 0.5 }}>
            Deployed Agents
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
            {agents.length} {agents.length === 1 ? 'agent' : 'agents'} deployed
          </Typography>
          </Box>
          <Button
            component={Link}
            to="/agent-studio"
            startIcon={<PsychologyIcon />}
            size="small"
            sx={{
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
              color: 'text.primary',
              fontSize: '0.75rem',
              py: 0.5,
              px: 1.5,
              '&:hover': {
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'
              }
            }}
          >
            Agent Studio
          </Button>
        </Box>
        
        <Box sx={{ 
          flex: 1, 
          overflowY: 'auto', 
          p: 2,
          '&::-webkit-scrollbar': {
            width: '0.4em'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
            borderRadius: 4
          }
        }}>
          {agents.length > 0 ? (
            agents.map((agent) => (
              <AgentItem key={agent.id || agent.name} agent={transformAgentData(agent)} theme={theme} />
            ))
          ) : (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: 'text.secondary',
              textAlign: 'center',
              p: 2
            }}>
              <Box sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2
              }}>
                <CheckCircleIcon sx={{ fontSize: 24, color: 'text.disabled' }} />
              </Box>
              <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>No agents deployed yet</Typography>
              <Typography variant="caption" sx={{ mb: 2, color: 'text.disabled' }}>
                Create and deploy AI agents to see them here
              </Typography>
              <Box
                component="button"
                onClick={() => navigate('/agent-studio')}
                sx={{
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                  color: 'text.primary',
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1.5,
                  px: 2,
                  py: 1,
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)',
                    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.2)'}`
                  }
                }}
              >
                Go to Agent Studio
              </Box>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
} 